import React from "react";
import { Trash2 } from "lucide-react";
import { isoDate, istToday, windowsLabel, weekdayKey } from "./scheduleUtils";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmtShort = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

// 4-week mini calendar (IST), starting this week's Monday. Dates with an override carry a marker.
export function OverridesCalendar({ overrides, weekly, onPick, onRemove }) {
  const today = istToday();
  const todayIso = isoDate(today);
  const monday = new Date(today); monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const days = Array.from({ length: 28 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return isoDate(d); });
  const byDate = Object.fromEntries(overrides.map((o) => [o.date, o]));
  const upcoming = overrides.filter((o) => o.date >= todayIso).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div data-testid="overrides-calendar">
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 mb-1">{WD.map((w) => <span key={w}>{w}</span>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((iso) => {
          const o = byDate[iso];
          const past = iso < todayIso;
          const off = o ? o.windows.length === 0 : (weekly[weekdayKey(iso)] || []).length === 0;
          return (
            <button key={iso} type="button" disabled={past} onClick={() => onPick(iso)} data-testid={`ovr-day-${iso}`} data-override={o ? "1" : "0"}
              title={o ? `Override: ${windowsLabel(o.windows)}` : windowsLabel(weekly[weekdayKey(iso)])}
              className={`relative rounded-lg border px-1 py-2 text-xs font-bold transition-colors ${past ? "text-slate-300 border-slate-100 bg-slate-50" : o ? (o.windows.length ? "border-[#D4AF37] bg-[#FBF4DF] text-[#0A2540]" : "border-rose-300 bg-rose-50 text-rose-700") : off ? "border-slate-100 text-slate-400 hover:border-slate-300" : "border-slate-200 text-slate-700 hover:border-[#0A2540]"} ${iso === todayIso ? "ring-2 ring-[#0A2540]/30" : ""}`}>
              {Number(iso.slice(-2))}
              {o && <span className={`absolute top-1 right-1 h-1.5 w-1.5 rounded-full ${o.windows.length ? "bg-[#D4AF37]" : "bg-rose-500"}`} />}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-400">
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#D4AF37]" /> Custom hours</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-500" /> Day off</span>
        <span>Greyed = no weekly hours</span>
      </div>

      <div className="mt-4" data-testid="overrides-list">
        <p className="text-xs font-bold text-slate-600 mb-2">Upcoming overrides {upcoming.length ? `(${upcoming.length})` : ""}</p>
        {upcoming.length === 0 && <p className="text-xs text-slate-400" data-testid="overrides-empty">None — every date follows your weekly schedule.</p>}
        <ul className="divide-y divide-slate-100">
          {upcoming.map((o) => (
            <li key={o.date} className="py-2 flex items-center gap-3 text-sm" data-testid={`override-item-${o.date}`}>
              <button type="button" onClick={() => onPick(o.date)} className="text-left flex-1 min-w-0">
                <p className="font-bold text-slate-800">{fmtShort(o.date)} <span className={`ml-2 text-[10px] uppercase tracking-wider font-bold ${o.windows.length ? "text-[#8a6d1a]" : "text-rose-600"}`}>{o.windows.length ? "custom hours" : "day off"}</span></p>
                <p className="text-xs text-slate-500 truncate">{windowsLabel(o.windows)}{o.reason ? ` · ${o.reason}` : ""}</p>
              </button>
              <button type="button" onClick={() => onRemove(o.date)} aria-label={`Remove override ${o.date}`} data-testid={`override-remove-${o.date}`} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
